import { useTheme } from "next-themes";
import { useState, useEffect, useRef } from "react";
import * as Icons from "./../components/letters";
import { SymbolIcon } from "./../components/letters";
import { getGlossary } from "../services/learn.service";
import { makeUniqueSlug, slugify, startsWithNonLetter } from "../utils/strings";
import Modal from "bootstrap/js/dist/modal";
import { customModalCancelButton, themeBasedGlossaryButton } from "../components/customStyles";
import { CrossIcon } from "../components/icons";
import { useDebounceFunction, useDebounceValue } from "../hooks/useDebounce";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "../hooks/useI18n";
import { useTranslation } from "react-i18next";

const Glossary = () => {
  const [letters, setLetters] = useState(() => {
    const letterArr = [];

    for (let i = 65; i <= 90; i++) {
      letterArr.push(String.fromCharCode(i));
    }

    return letterArr;
  });

  const [activeGlossaryIndex, setActiveGlossaryIndex] = useState("All");
  const [searchGlossary, setSearchGlossary] = useState("");
  const [scrollingLeft, setScrollingLeft] = useState(false);
  const [scrollingRight, setScrollingRight] = useState(true);
  const [activeGlossaryItem, setActiveGlossaryItem] = useState(null);
  const [fetchingGlossary, setFetchingGlossary] = useState(false);
  const [retryScrollCount, setRetryScrollCount] = useState(0);

  const [glossaryData, setGlossaryData] = useState([]);

  const [screenSize, setScreenSize] = useState();

  // const activeGlossaryItems = activeGlossaryIndex ? glossaryData.filter(item => item.title.startsWith(activeGlossaryIndex)) : null;

  const { theme } = useTheme();
  const { isRTL } = useI18n();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get("search");
  const searchQuery = searchParams.get("query");

  const glossaryModalRef = useRef(null);
  const glossaryModalInstanceRef = useRef(null);
  const lettersScrollable = useRef(null);

  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);

  let searchLetters = searchGlossary ? [] : letters;

  if (searchGlossary) {
    for (const item of glossaryData) {
      if (!startsWithNonLetter(item.title.trim())) {
        if (!searchLetters.includes(item.title.trim().at(0))) {
          searchLetters.push(item.title.trim().at(0));
        }
      } else {
        if (!searchLetters.includes("#")) {
          searchLetters.push("#");
        }
      }
    }

    searchLetters = searchLetters.sort((a, b) => a.localeCompare(b));

    if (searchLetters.includes("#")) {
      const temp = searchLetters.slice(1);
      temp.push("#");
      searchLetters = temp;
    }
  }

  const debouncedSearch = useDebounceValue(searchGlossary, 500);

  const handleLeftScroll = () => {
    lettersScrollable.current.scrollLeft -= 60;
  };

  const handleRightScroll = () => {
    lettersScrollable.current.scrollLeft += 60;
  };

  const handleActiveGlossaryIndex = (letter) => {
    setActiveGlossaryIndex(letter);
    // if (activeGlossaryIndex === letter) {
    //     setActiveGlossaryIndex("");
    // } else {
    // }
  };

  const scrollEventHandler = () => {
    const scrollContainer = lettersScrollable.current;

    if (scrollContainer.scrollLeft > 0) {
      setScrollingLeft(true);
    } else {
      setScrollingLeft(false);
    }

    if (Math.ceil(scrollContainer.clientWidth + scrollContainer.scrollLeft) >= scrollContainer.scrollWidth) {
      setScrollingRight(false);
    } else {
      setScrollingRight(true);
    }
  };

  const attachScrollListener = () => {
    const scrollContainer = lettersScrollable.current;

    if (!(scrollContainer.clientWidth == scrollContainer.scrollWidth)) {
      scrollEventHandler();
    }

    scrollContainer.addEventListener("scroll", scrollEventHandler);
  };

  const fetchGlossaryData = async (title, letter) => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    const thisRequestId = ++requestIdRef.current;

    setFetchingGlossary(true);

    return getGlossary(title, letter, { signal: controller.signal })
    .then((response) => {
      // only update if this is still the latest request
      if (thisRequestId === requestIdRef.current) {
        if (!response.error) {
          setGlossaryData(response.data);
        } else {
          setGlossaryData([]);
        }
      }
    })
    .catch((err) => {
      // ignore aborts; other errors only handled if still latest
      if (err.name === "AbortError") return;
      if (thisRequestId === requestIdRef.current) {
        setGlossaryData([]);
      }
    })
    .finally(() => {
      // only the latest request may turn the loader off
      if (thisRequestId === requestIdRef.current) {
        setFetchingGlossary(false);
      }
      // cleanup controllerRef only if it's ours
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    });

    // try {
    //   const response = await getGlossary(title, letter, {
    //     signal: controller.signal,
    //   });

    //   if (thisRequestId === requestIdRef.current) {
    //     if (!response.error) {
    //       setGlossaryData(response.data);
    //     } else {
    //       setGlossaryData([]);
    //     }
    //   }
    // } catch (err) {
    //   if (err.name === "AbortError") {
    //   }

    //   // console.error("Glossary fetch failed:", err);
    //   if (thisRequestId === requestIdRef.current) {
    //     setGlossaryData([]);
    //   }
    // } finally {
    //   if (controllerRef.current === controller) {
    //     controllerRef.current = null;
    //   }
    // }
  };

  const viewGlossaryItem = (item) => {
    const { title, description } = item;

    setActiveGlossaryItem({ title, description });

    toggleGlossaryModal();
  };

  const toggleGlossaryModal = () => {
    glossaryModalInstanceRef.current.toggle();
  };

  useEffect(() => {
    fetchGlossaryData();

    if (glossaryModalRef.current) {
      glossaryModalInstanceRef.current = new Modal(glossaryModalRef.current, { keyboard: true, backdrop: true });
    }

    const tabsRowScrollHandler = () => {
      const scrollContainer = lettersScrollable.current;

      if (scrollContainer.scrollLeft > 0) {
        setScrollingLeft(true);
      } else {
        setScrollingLeft(false);
      }

      if (Math.ceil(scrollContainer.clientWidth + scrollContainer.scrollLeft) >= scrollContainer.scrollWidth) {
        setScrollingRight(false);
      } else {
        setScrollingRight(true);
      }
    };

    const resizeScreen = () => {
      setScreenSize(window?.innerWidth);
      // if (screenSize <= 1430) tabsRowScrollHandler();
    };

    resizeScreen();
    attachScrollListener();
    // if (screenSize <= 1430) tabsRowScrollHandler();

    window.addEventListener("resize", resizeScreen);
    lettersScrollable.current.addEventListener("scroll", tabsRowScrollHandler);

    return () => {
      window.removeEventListener("resize", resizeScreen);
      if (lettersScrollable.current) {
        lettersScrollable.current.removeEventListener("scroll", tabsRowScrollHandler);
      }
    };
  }, []);

  useEffect(() => {
    // if (debouncedSearch.trim() === '' && activeGlossaryIndex === '') return;

      fetchGlossaryData(searchGlossary, activeGlossaryIndex === "All" ? "" : activeGlossaryIndex);
  }, [debouncedSearch, activeGlossaryIndex]);

  useEffect(() => {
    if (searchTerm && !fetchingGlossary) {
      const scrollToElement = () => {
        const element = document.getElementById(searchTerm);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (retryScrollCount < 5) {
          setTimeout(() => {
            setRetryScrollCount((prev) => prev + 1);
            scrollToElement();
          }, 200);
        }
      };

      setTimeout(scrollToElement, 200);
    }
  }, [searchTerm, fetchingGlossary]);

  return (
    <div
      className='row w-100 mx-auto flex-column px-3 px-sm-4 pt-2'
      style={{
        maxWidth: "1300px",
      }}
    >
      <div className='col-12 p-0'>
        <div className='row w-100 mx-auto align-items-center flex-md-row-reverse flex-md-nowrap'>
          <div className='col-12 col-md-auto p-0 pe-2'>
            <div
              className='position-relative'
              style={{
                "--bs-border-color": "var(--theme-light-primary)",
                "--bs-body-bg": "rgba(255,255,255,0.8)",
              }}
            >
              <input
                type='text'
                className='form-control'
                placeholder={t("app.messages.search.glossary")}
                value={searchGlossary}
                data-testid="search-input"
                onChange={(e) => setSearchGlossary(e.target.value)}
                aria-label='Search Term ...'
                aria-describedby='button-addon2'
                style={{
                  minWidth: "260px",
                  paddingInline: isRTL ? "2.3rem .75rem" : ".75rem 2.3rem",
                }}
              />
              <button
                className='btn position-absolute h-100 border-0'
                type='button'
                id='button-addon2'
                style={{
                  top: "0",
                  right: "0",
                  padding: ".25rem .5rem .45rem",
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={22}
                  height={22}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='var(--theme-light-secondary)'
                  strokeWidth={1.5}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-search'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
                  <path d='M21 21l-6 -6' />
                </svg>
              </button>
            </div>
          </div>
          <div className='col-12 col-md p-0 position-relative mt-3 mt-md-0'>
            {scrollingLeft ? (
              <button
                className='btn btn-primary border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                onClick={() => handleLeftScroll()}
                style={{
                  top: "1px",
                  insetInlineStart: "0",
                  height: "34px",
                  width: "34px",
                  padding: "0 2px 0 0",
                  // color: 'var(--theme-light-secondary)',
                  // padding: '2px',
                  // border: '1px solid var(--theme-light-primary)',
                  // borderRadius: '50%',
                  // background: theme === 'dark' ? 'rgb(46, 51, 58)' : 'rgb(255, 255, 255)',
                  // boxShadow: theme === 'dark' ? 'rgb(46, 51, 58) 0px 0px 12px 14px, rgb(46, 51, 58) -13px 0px 4px 8px' : 'var(--theme-light-primary) 0px 0px 12px 14px',
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={30}
                  height={30}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M15 6l-6 6l6 6' />
                </svg>
              </button>
            ) : (
              ""
            )}

            {scrollingRight ? (
              <button
                className='btn btn-primary border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                onClick={() => handleRightScroll()}
                style={{
                  top: "1px",
                  insetInlineEnd: "12px",
                  height: "34px",
                  width: "34px",
                  padding: "0 0 0 2px",
                  // top: '3px',
                  // right: '6px',
                  // color: 'var(--theme-light-secondary)',
                  // padding: '2px',
                  // background: theme === 'dark' ? 'rgb(58, 51, 59)' : 'rgb(243, 226, 246)',
                  // boxShadow: theme === 'dark' ? 'rgb(58, 51, 59) 0px 0px 12px 14px, rgb(58, 51, 59) 13px 0px 4px 8px' : 'rgb(243, 226, 246) 0px 0px 12px 14px, rgb(243, 226, 246) 13px 0px 4px 8px'
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={30}
                  height={30}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M9 6l6 6l-6 6' />
                </svg>
              </button>
            ) : (
              ""
            )}

            <div
              className='d-inline-flex align-items-center justify-content-start gap-1 pb-1 gloss_container'
              ref={lettersScrollable}
              style={{
                lineHeight: "1",
                borderBottom: `1px solid ${theme === "light" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,.3)"}`,
                maxWidth:
                  screenSize >= 1300
                    ? "calc(1300px - 335px)"
                    : screenSize <= 767 && screenSize >= 576
                      ? "calc(100vw - 77px)"
                      : screenSize <= 576
                        ? "calc(100vw - 58px)"
                        : "calc(100vw - 344px)",
                scrollbarWidth: "none",
                overflowX: "auto",
              }}
            >
              <button
                className='btn m-0'
                type='button'
                onClick={() => setActiveGlossaryIndex("All")}
                data-letter={"All"}
                style={{
                  ...themeBasedGlossaryButton(activeGlossaryIndex, theme, "All"),
                  padding: "0.275rem 0.55rem",
                  fontSize: "1.1rem",
                  minWidth: "44px",
                  backgroundColor: activeGlossaryIndex === "All" ? (theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)") : "",
                  color: activeGlossaryIndex === "All" ? (theme === "dark" ? "rgba(255,255,255,.9)" : "rgba(255,255,255,.9)") : "",
                }}
              >
                All
              </button>
              {letters.map((letter, index) => {
                // const letter = String.fromCodePoint(code);
                return (
                  <button
                    className='btn m-0'
                    type='button'
                    key={index}
                    data-letter={letter}
                    onClick={() => handleActiveGlossaryIndex(letter)}
                    style={{
                      ...themeBasedGlossaryButton(activeGlossaryIndex, theme, letter),
                      // backgroundColor: activeGlossaryIndex === letter && (theme === 'light' ? 'rgba(0,0,0,0.6)' : 'var(--theme-light-secondary)'),
                      // color: activeGlossaryIndex === letter ? (theme === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.9)') : (theme === 'light' ? 'rgb(0,0,0)' : 'rgba(255,255,255,.9)'),
                      padding: "0.275rem 0.55rem",
                      // border: '1px solid #000',
                      fontSize: "1.1rem",
                      minWidth: "38px",
                      backgroundColor: activeGlossaryIndex === letter ? (theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)") : "",
                      color: activeGlossaryIndex === letter ? (theme === "dark" ? "rgba(255,255,255,.9)" : "rgba(255,255,255,.9)") : "",
                    }}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className='col-12 p-0'>
        <div
          className={`row w-100 mx-auto flex-column justify-content-start flex-nowrap add-custom-scroll${theme === "dark" ? "-dark" : ""}`}
          style={{
            height:
              screenSize <= 575
                ? "calc(100dvh - 302px)"
                : screenSize >= 576 && screenSize <= 767
                  ? "calc(100dvh - 356px)"
                  : screenSize >= 992
                    ? "calc(100dvh - 228px)"
                    : "calc(100dvh - 302px)",
            overflowY: "auto",
            scrollbarWidth: screenSize >= 768 ? "" : "none",
          }}
        >
          {!fetchingGlossary ? (
            <>
              {activeGlossaryIndex === "All" ? (
                <>
                  {glossaryData.length ? (
                    <>
                      {searchGlossary
                        ? searchLetters.map((letter, index) => {
                            let letterGlossaryItems = [];

                            if (letter === "#") {
                              letterGlossaryItems = glossaryData.filter((item) => startsWithNonLetter(item.title.trim()));
                            } else {
                              letterGlossaryItems = glossaryData.filter((item) => item.title.trim().startsWith(letter));
                            }

                            const LetterIcon = Icons[`${letter}Icon`];

                            return (
                              <div className='col-12 p-0' key={index}>
                                <div className='row w-100 mx-auto pt-2 justify-content-center'>
                                  <div className='col-auto col-sm-auto p-0 d-flex justify-content-end'>
                                    {LetterIcon ? (
                                      <LetterIcon height={150} width={150} color={theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)"} />
                                    ) : (
                                      <SymbolIcon height={150} width={150} color={theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)"} />
                                    )}
                                  </div>
                                  <div className='col-12 col-sm p-0'>
                                    <div className='row w-100 mx-auto'>
                                      {letterGlossaryItems.length
                                        ? letterGlossaryItems.map((glossItem, idx) => (
                                            <div className='col-12 col-md-6 col-xl-4 p-2' key={idx} style={{}}>
                                              <div
                                                className='p-3'
                                                onClick={() => viewGlossaryItem(glossItem)}
                                                style={{
                                                  cursor: "pointer",
                                                  border: "1px solid rgba(0,0,0,0.3)",
                                                  borderRadius: "10px",
                                                  backgroundColor: theme === "light" ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.7)",
                                                  minHeight: "225px",
                                                }}
                                              >
                                                <div
                                                  className=''
                                                  style={{
                                                    fontSize: "1.45rem",
                                                    fontWeight: "500",
                                                    lineHeight: "1",
                                                    minHeight: "51px",
                                                  }}
                                                >
                                                  {glossItem.title}
                                                </div>
                                                <div
                                                  className='mt-2'
                                                  style={{
                                                    fontSize: "0.9rem",
                                                    fontWeight: "400",
                                                    color: "rgba(0,0,0,0.6)",
                                                    display: "-webkit-box",
                                                    ["-webkit-box-orient"]: "vertical",
                                                    WebkitLineClamp: "6",
                                                    overflow: "hidden",
                                                  }}
                                                >
                                                  {glossItem.description}
                                                </div>
                                              </div>
                                            </div>
                                          ))
                                        : ""}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        : letters.map((letter, index) => {
                            let letterGlossaryItems = [];

                            if (letter === "#") {
                              letterGlossaryItems = glossaryData.filter((item) => startsWithNonLetter(item.title.trim()));
                            } else {
                              letterGlossaryItems = glossaryData.filter((item) => item.title.trim().startsWith(letter));
                            }

                            const LetterIcon = Icons[`${letter}Icon`];

                            return (
                              <div className='col-12 p-0' key={index}>
                                <div className='row w-100 mx-auto pt-2 justify-content-center'>
                                  <div className='col-auto col-sm-auto p-0 d-flex justify-content-end'>
                                    {LetterIcon ? (
                                      <LetterIcon height={150} width={150} color={theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)"} />
                                    ) : (
                                      <SymbolIcon height={150} width={150} color={theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)"} />
                                    )}
                                  </div>
                                  <div className='col-12 col-sm p-0'>
                                    <div className='row w-100 mx-auto'>
                                      {letterGlossaryItems.length
                                        ? letterGlossaryItems.map((glossItem, idx) => (
                                            <div className='col-12 col-md-6 col-xl-4 p-2' id={slugify(glossItem.title)} key={idx} style={{}}>
                                              <div
                                                className='p-3'
                                                onClick={() => viewGlossaryItem(glossItem)}
                                                style={{
                                                  cursor: "pointer",
                                                  border: "1px solid rgba(0,0,0,0.3)",
                                                  borderRadius: "10px",
                                                  backgroundColor: theme === "light" ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.7)",
                                                  minHeight: "225px",
                                                }}
                                              >
                                                <div
                                                  className=''
                                                  style={{
                                                    fontSize: "1.45rem",
                                                    fontWeight: "500",
                                                    lineHeight: "1",
                                                    minHeight: "51px",
                                                  }}
                                                >
                                                  {glossItem.title}
                                                </div>
                                                <div
                                                  className='mt-2'
                                                  style={{
                                                    fontSize: "0.9rem",
                                                    fontWeight: "400",
                                                    color: "rgba(0,0,0,0.6)",
                                                    display: "-webkit-box",
                                                    ["-webkit-box-orient"]: "vertical",
                                                    WebkitLineClamp: "6",
                                                    overflow: "hidden",
                                                  }}
                                                >
                                                  {glossItem.description}
                                                </div>
                                              </div>
                                            </div>
                                          ))
                                        : ""}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                    </>
                  ) : (
                    ""
                  )}
                </>
              ) : (
                (() => {
                  const LetterIcon = Icons[`${activeGlossaryIndex}Icon`];

                  const localData = searchGlossary ? glossaryData.filter((item) => item.title.trim().startsWith(activeGlossaryIndex)) : glossaryData;

                  return (
                    <div className='col-12 p-0'>
                      <div className='row w-100 mx-auto pt-2 justify-content-center'>
                        <div className='col-auto col-sm-auto p-0 d-flex justify-content-end'>
                          <LetterIcon height={150} width={150} color={theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)"} />
                        </div>
                        <div className='col-12 col-sm p-0'>
                          <div className='row w-100 mx-auto'>
                            {glossaryData.map((glossItem, index) => (
                              <div className='col-12 col-md-6 col-xl-4 p-2' key={index} id={slugify(glossItem.title)} style={{}}>
                                <div
                                  className='p-3'
                                  onClick={() => viewGlossaryItem(glossItem)}
                                  style={{
                                    cursor: "pointer",
                                    border: "1px solid rgba(0,0,0,0.3)",
                                    borderRadius: "10px",
                                    backgroundColor: theme === "light" ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.7)",
                                    minHeight: "225px",
                                  }}
                                >
                                  <div
                                    className=''
                                    style={{
                                      fontSize: "1.45rem",
                                      fontWeight: "500",
                                      lineHeight: "1",
                                      minHeight: "51px",
                                    }}
                                  >
                                    {glossItem.title}
                                  </div>
                                  <div
                                    className='mt-2'
                                    style={{
                                      fontSize: "0.9rem",
                                      fontWeight: "400",
                                      color: "rgba(0,0,0,0.6)",
                                      display: "-webkit-box",
                                      ["-webkit-box-orient"]: "vertical",
                                      WebkitLineClamp: "6",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {glossItem.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </>
          ) : (
            <div
              className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
              data-testid="glossary-loader"
              style={{
                color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
              }}
            >
              <div
                className=''
                role='status'
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "300",
                  letterSpacing: isRTL ? "" : ".02em",
                }}
              >
                {"Loading Glossary ..."}
              </div>
              <div
                className='spinner-border'
                style={{
                  fontSize: "11px",
                  height: "24px",
                  width: "24px",
                  color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                }}
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* -- GLOSSARY ITEM VIEW - MODAL -- */}
      <div className='modal fade' id='glossaryModal' tabIndex='-1' aria-labelledby='glossaryModal' aria-hidden='true' ref={glossaryModalRef}>
        <div
          className='modal-dialog modal-dialog-centered m-2 mx-md-auto'
          style={{
            ["--bs-modal-width"]: "500px",
          }}
        >
          <div
            className='modal-content position-relative'
            style={{
              backgroundColor: theme === "light" ? "rgb(230 230 230)" : "rgb(50 ,50 ,50)",
              color: theme === "light" ? "rgb(0,0,0)" : "rgb(232, 232, 232)",
            }}
          >
            <div
              className='position-absolute top-0 end-0'
              style={{
                zIndex: "100",
              }}
            >
              <button
                type='button'
                className='btn p-1'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  border: "none",
                  boxShadow: "none",
                }}
                onClick={() => toggleGlossaryModal()}
              >
                <CrossIcon width={32} height={32} color={theme === "dark" ? "rgba(225,225,225,.7)" : "rgba(34, 34, 34, 0.8)"} />
              </button>
            </div>

            <div className='modal-body'>
              <div
                className='d-flex text-center flex-column align-items-center justify-content-center flex-nowrap row-gap-3 pt-4 pb-1 px-2'
                style={{
                  minHeight: "160px",
                }}
              >
                <div
                  className=''
                  style={{
                    color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                    fontSize: "1.6rem",
                    fontWeight: "500",
                    lineHeight: "1.15",
                    maxWidth: "400px",
                  }}
                >
                  {activeGlossaryItem?.title}
                </div>
                <div
                  className=''
                  style={{
                    color: theme === "dark" ? "rgba(255,255,255,.75)" : "rgba(0,0,0,.75)",
                  }}
                >
                  {activeGlossaryItem?.description}
                </div>
              </div>
            </div>
            <div className='modal-footer border-0 justify-content-center'>
              <button
                type='button'
                className='btn border-0'
                onClick={() => toggleGlossaryModal()}
                style={{
                  // ["--bs-btn-bg"]: 'rgb(79 56 80)',
                  ...customModalCancelButton,
                  // backgroundColor: 'rgb(100 83 100)',
                  minWidth: "90px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Glossary;
